import {Injectable} from '@angular/core';
import {Order} from '../classes/order';
import {Mobile} from '../classes/mobile';
import * as moment from 'moment';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {environment} from '../../../environments/environment';
import {BehaviorSubject} from 'rxjs';
import { AuthService } from 'src/app/shared/services/auth.service';
import { fillZipCode, getNameStreet } from 'src/app/utils/normalizerFunctions';
import { RepublicaMovilService } from './republica-movil.service';

@Injectable({
    providedIn: 'root'
})
export class RmOrderService {
    private roadTypes = [];
    private order = new Order();
    private orderObservable: any;

    constructor(
        private http: HttpClient, 
        private auth: AuthService,
        private rmService: RepublicaMovilService
    ) {
        this.orderObservable = new BehaviorSubject<Order>(this.order);

        this.rmService.getRoadTypes()
            .subscribe((data: any) => {
                if (data != null) {
                    this.roadTypes = data;
                }
            });
    }

    getHeaders() {
        const token = 'Bearer ' + this.auth.getToken();

        return new HttpHeaders({
            Authorization: token,
            'Content-Type': 'application/json'
        });
    }


    createOrder(draft: boolean = true, info?) {
        if (info) {
            info.draft = draft;
            info.order_id = this.order && this.order.order_id ? this.order.order_id : 'no tiene';
        } else {
            info.trace = 'Esto no se de donde viene'
        }
        this.saveLog(info);
        this.order.draft = draft;
        this.order.agreements.privacy_policy = true;

        return this.http.post(environment.rmEndpoint + 'order/create', this.order, {headers: this.getHeaders()});
    }

    saveLog(info) {
        this.http.post(environment.rmEndpoint + 'log/duplicated/order/create', info, {headers: this.getHeaders()}).subscribe(data => {});
    }

    getOrderFromService(id) {
        return this.http.get(environment.rmEndpoint + 'order/' + id, {headers: this.getHeaders()});
    }

    setAgreements(data) { 
        this.order.agreements.commercial_communications = data.check1;
        this.setOrder(this.order);
    }

    setOrder(data) {
        this.orderObservable.next(data);
    }

    getOrder() {
        return this.orderObservable.asObservable();
    }

    setOrderId(orderId: any) {
        this.order.order_id = orderId;
        this.setOrder(this.order);
    }

    setBroadbandRate(rate: any) {
        this.order.broadband.rate_package_id = rate.packageId;
        this.setOrder(this.order);
    }

    setBroadbandCoverage(data: any) {
        this.order.broadband.arvato_code = data.resultadoConsultaCobertura && data.resultadoConsultaCobertura !== "" ? data.resultadoConsultaCobertura.cimaArvatoCode : '';
        this.order.broadband.gescal_code = data.resultadoConsultaCobertura && data.resultadoConsultaCobertura !== "" ? data.resultadoConsultaCobertura.cimaGescalCode : '';
        this.order.broadband.province = data.provinciaCobertura;
        this.order.broadband.city = data.localidadCobertura;
        this.order.broadband.post_code = fillZipCode(data.codigoPostalCobertura);
        this.order.broadband.street_number = data.numeroCobertura;
        this.order.broadband.street_name = data.nombreViaCobertura;
        this.order.broadband.street_type = this.getCodeStreetType(data.tipoViaCobertura);
        this.order.broadband.staircase_number = data.escaleraCobertura;
        this.order.broadband.floor_number = data.plantaCobertura ? data.plantaCobertura.slice(0, 10) : data.plantaCobertura;
        this.order.broadband.apartment_number = data.puertaCobertura;
        this.setOrder(this.order);
    }

    setMobiles(array: any[]) {
        this.order.mobiles = [];

        array.forEach((item: any, index: number) => {
            const mobile = new Mobile();

            mobile.rate_package_id = item.rate ? item.rate.packageId : null;
            mobile.contract_type = item.rate ? item.rate.contractType : null;
            mobile.msisdn = item.portability_phone;
            mobile.old_iccid = item.portability_iccid;
            mobile.old_contract_type = item.portability_type_client;
            mobile.new_iccid = item.iccid;
            mobile.principal = index === 0;
            mobile.service_provider_id = item.portability_operator;
            mobile.processing_date = item.portability_date !== '' ? moment(item.portability_date).format('YYYY-MM-DD') : '';
            mobile.sponsor_phone = '';
            mobile.sponsor = item.sponsor;
            if (typeof this.order.broadband !== 'undefined' &&
                typeof this.order.broadband.city !== 'undefined' &&
                this.order.broadband.city !== '')
            {
                mobile.convergent  = true;
            } else {
                mobile.convergent  = false;
                this.order.broadband = undefined;
            }
            this.order.mobiles.push(mobile);
        });

        this.setOrder(this.order);
    }

    setUserData(data: any) {
        this.order.customer.name = data.firstName;
        this.order.customer.last_name = data.surname1;
        this.order.customer.second_last_name = data.surname2;
        this.order.customer.phone_number = data.phone;
        this.order.customer.email = data.email;
        this.order.customer.document_type = data.doctype;
        this.order.customer.document = data.doc.replace(/-/g, '');
        this.order.customer.nationality = data.country;
        let birthday = null;
        if (data.birthday) {
            birthday = moment(data.birthday).format('YYYY-MM-DD');
        }
        this.order.customer.birth_date = birthday;
        this.order.customer.gender = data.sex;
        this.order.customer.company_name = data.company ? data.company : '';

        // Payment
        this.order.customer.payment.iban = data.bank_account.replace(/\s/g, '');
        this.order.customer.payment.document_type = data.account_doctype;
        this.order.customer.payment.document = data.account_doc.replace(/-/g, '');
        this.order.customer.payment.country = data.account_country;
        this.order.customer.payment.name = data.account_firstName;
        this.order.customer.payment.last_name = data.account_surname1;
        this.order.customer.payment.second_last_name = data.account_surname2;
        this.order.customer.payment.company = data.account_company;

        // Billing Address
        this.order.customer.billing_address.province = data.provincia;
        this.order.customer.billing_address.city = data.localidad;
        this.order.customer.billing_address.post_code = fillZipCode(data.codigoPostal);
        this.order.customer.billing_address.street_type = this.getCodeStreetType(data.tipoVia);
        this.order.customer.billing_address.street_name = getNameStreet(data.nombreVia, this.roadTypes, 'code');
        this.order.customer.billing_address.additional_info = '';
        this.order.customer.billing_address.street_number = data.numero;
        this.order.customer.billing_address.staircase_number = data.escalera;
        this.order.customer.billing_address.floor_number = data.planta ? data.planta.slice(0, 10) : data.planta;
        this.order.customer.billing_address.apartment_number = data.puerta;

        // Pioneros
        if (this.order.broadband) {
            this.order.broadband.sponsor = data.sponsor;
        } else if (this.order.mobiles && this.order.mobiles.length) {
            this.order.mobiles[0].sponsor = data.sponsor;
        }

        this.setOrder(this.order);
    }

    removeOrder(index) {
        this.order.mobiles.splice(index, 1);
        this.setOrder(this.order);
    }

    getCodeStreetType(value): string {
        if (this.roadTypes && this.roadTypes.length && value) {
            if (value.toLowerCase() === 'passatge') {
                value = 'pasaje';
            }
            const found = this.roadTypes.find(x => x.code.toLowerCase() === value.toLowerCase());
            if (found) {
                return found.code;
            } else {
                return value;
            }
        }
        return value;
    }

    resetOrder() {
        this.order = new Order();
    }
}
